if (!customElements.get('prov4-customizations')) {
  customElements.define(
    'prov4-customizations',
    class Prov4Customizations extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.shopDomain = this.dataset.shopDomain || '';
        this.shopIdPath = this.dataset.shopIdPath || '';
        this.customizationData = null;
        this.currentSelections = {
          font: '01',
          mainColor: 'DB',
          borderColor: 'DB',
          style: '01'
        };
        
        this.init();
      }

      async init() {
        // Load customization data
        const dataScript = document.getElementById('prov4-customization-data');
        if (dataScript) {
          this.customizationData = JSON.parse(dataScript.textContent);
        }

        // Randomize defaults if desired (optional - can be enabled)
        this.randomizeDefaults();

        // Render all selectors
        this.renderFontSelector();
        this.updateStyleSelector();
        this.renderMainColorSelector();
        this.renderBorderColorSelector();
        this.updateProductImage();

        // Show/hide border color selector based on initial main color selection
        this.toggleBorderColorSelector(this.currentSelections.mainColor === 'US');

        // Set up event listeners
        this.setupEventListeners();
        
        // Set up decal sizing selector
        this.setupDecalSizingSelector();
        
        // Set up variant change listener for conditional field display
        this.setupVariantChangeListener();
        
        // Check initial variant state
        this.updateFieldsBasedOnVariant();
        
        // Set up sticky image behavior on mobile
        this.setupStickyImage();
      }

      randomizeDefaults() {
        // Randomize font (01-20)
        const randomFont = String(Math.floor(Math.random() * 20) + 1).padStart(2, '0');
        
        // Randomize main color (excluding US for border, but including for main)
        const mainColorIds = this.customizationData.mainColors.map(c => c.id);
        const randomMainColor = mainColorIds[Math.floor(Math.random() * mainColorIds.length)];
        
        // Randomize border color (excluding US)
        const borderColorIds = this.customizationData.borderColors.map(c => c.id);
        const randomBorderColor = borderColorIds[Math.floor(Math.random() * borderColorIds.length)];
        
        // Randomize style
        const styleIds = this.customizationData.styles.map(s => s.id);
        const randomStyle = styleIds[Math.floor(Math.random() * styleIds.length)];

        this.currentSelections = {
          font: randomFont,
          mainColor: randomMainColor,
          borderColor: randomBorderColor,
          style: randomStyle
        };

        // Update hidden inputs
        this.updateHiddenInputs();
      }

      renderFontSelector() {
        const container = this.querySelector('[data-option="font"]');
        if (!container || !this.customizationData) return;

        container.innerHTML = '';
        this.customizationData.fonts.forEach((font, index) => {
          const isSelected = font.id === this.currentSelections.font;
          const inputId = `prov4-font-${this.sectionId}-${index}`;
          
          const input = document.createElement('input');
          input.type = 'radio';
          input.id = inputId;
          input.name = `font-${this.sectionId}`;
          input.value = font.id;
          input.checked = isSelected;
          input.classList.add('swatch-input__input');
          input.setAttribute('data-customization-value', font.id);
          input.setAttribute('data-customization-type', 'font');

          const label = document.createElement('label');
          label.htmlFor = inputId;
          label.classList.add('swatch-input__label', 'swatch-input__label--square');
          label.title = font.name;

          const swatch = document.createElement('span');
          swatch.classList.add('swatch', 'swatch--square');
          const swatchUrl = this.getShopifyFileUrl(font.filename);
          swatch.style.setProperty('--swatch--background', `url(${swatchUrl})`);
          swatch.style.backgroundImage = `url(${swatchUrl})`;
          swatch.style.backgroundSize = 'cover';
          swatch.style.backgroundPosition = 'center';

          label.appendChild(swatch);
          container.appendChild(input);
          container.appendChild(label);
        });

        this.updateSelectedValue('font', this.currentSelections.font);
      }

      renderMainColorSelector() {
        const container = this.querySelector('[data-option="mainColor"]');
        if (!container || !this.customizationData) return;

        container.innerHTML = '';
        this.customizationData.mainColors.forEach((color, index) => {
          const isSelected = color.id === this.currentSelections.mainColor;
          const inputId = `prov4-main-color-${this.sectionId}-${index}`;
          
          const input = document.createElement('input');
          input.type = 'radio';
          input.id = inputId;
          input.name = `main-color-${this.sectionId}`;
          input.value = color.id;
          input.checked = isSelected;
          input.classList.add('swatch-input__input');
          input.setAttribute('data-customization-value', color.id);
          input.setAttribute('data-customization-type', 'mainColor');

          const label = document.createElement('label');
          label.htmlFor = inputId;
          label.classList.add('swatch-input__label', 'swatch-input__label--square');
          label.title = color.name;

          const swatch = document.createElement('span');
          swatch.classList.add('swatch', 'swatch--square');
          const swatchUrl = this.getShopifyFileUrl(color.filename);
          swatch.style.setProperty('--swatch--background', `url(${swatchUrl})`);
          swatch.style.backgroundImage = `url(${swatchUrl})`;
          swatch.style.backgroundSize = 'cover';
          swatch.style.backgroundPosition = 'center';

          label.appendChild(swatch);
          container.appendChild(input);
          container.appendChild(label);
        });

        const selectedColor = this.customizationData.mainColors.find(c => c.id === this.currentSelections.mainColor);
        this.updateSelectedValue('mainColor', selectedColor ? selectedColor.name : '');
      }

      renderBorderColorSelector() {
        const container = this.querySelector('[data-option="borderColor"]');
        if (!container || !this.customizationData) return;

        container.innerHTML = '';
        this.customizationData.borderColors.forEach((color, index) => {
          const isSelected = color.id === this.currentSelections.borderColor;
          const inputId = `prov4-border-color-${this.sectionId}-${index}`;
          
          const input = document.createElement('input');
          input.type = 'radio';
          input.id = inputId;
          input.name = `border-color-${this.sectionId}`;
          input.value = color.id;
          input.checked = isSelected;
          input.classList.add('swatch-input__input');
          input.setAttribute('data-customization-value', color.id);
          input.setAttribute('data-customization-type', 'borderColor');

          const label = document.createElement('label');
          label.htmlFor = inputId;
          label.classList.add('swatch-input__label', 'swatch-input__label--square');
          label.title = color.name;

          const swatch = document.createElement('span');
          swatch.classList.add('swatch', 'swatch--square');
          const swatchUrl = this.getShopifyFileUrl(color.filename);
          swatch.style.setProperty('--swatch--background', `url(${swatchUrl})`);
          swatch.style.backgroundImage = `url(${swatchUrl})`;
          swatch.style.backgroundSize = 'cover';
          swatch.style.backgroundPosition = 'center';

          label.appendChild(swatch);
          container.appendChild(input);
          container.appendChild(label);
        });

        const selectedColor = this.customizationData.borderColors.find(c => c.id === this.currentSelections.borderColor);
        this.updateSelectedValue('borderColor', selectedColor ? selectedColor.name : '');
      }

      updateStyleSelector() {
        const container = this.querySelector('[data-option="style"]');
        if (!container || !this.customizationData) return;

        container.innerHTML = '';
        this.customizationData.styles.forEach((style, index) => {
          const isSelected = style.id === this.currentSelections.style;
          const inputId = `prov4-style-${this.sectionId}-${index}`;
          
          const input = document.createElement('input');
          input.type = 'radio';
          input.id = inputId;
          input.name = `style-${this.sectionId}`;
          input.value = style.id;
          input.checked = isSelected;
          input.classList.add('swatch-input__input');
          input.setAttribute('data-customization-value', style.id);
          input.setAttribute('data-customization-type', 'style');

          const label = document.createElement('label');
          label.htmlFor = inputId;
          label.classList.add('swatch-input__label', 'swatch-input__label--square');
          label.title = style.name;

          // Style swatch image is dynamic based on style ID, main color, and border color
          // Each style option shows its own unique swatch image
          // Format: ProV4-StyleSwatch-{STYLEID}-{MAINCOLORID}-{BORDERCOLORID}.jpg
          const swatch = document.createElement('span');
          swatch.classList.add('swatch', 'swatch--square', 'prov4-style-swatch');
          swatch.setAttribute('data-style-id', style.id);
          
          // Build style swatch URL for this specific style (includes style ID, main color, border color)
          // When main color is US, border color is not used in the filename
          const styleSwatchUrl = this.getStyleSwatchUrl(
            style.id,
            this.currentSelections.mainColor,
            this.currentSelections.mainColor === 'US' ? '' : this.currentSelections.borderColor
          );
          
          swatch.style.setProperty('--swatch--background', `url(${styleSwatchUrl})`);
          swatch.style.backgroundImage = `url(${styleSwatchUrl})`;
          swatch.style.backgroundSize = 'cover';
          swatch.style.backgroundPosition = 'center';

          label.appendChild(swatch);
          container.appendChild(input);
          container.appendChild(label);
        });

        // Update selected value display with display name
        const selectedStyle = this.customizationData.styles.find(s => s.id === this.currentSelections.style);
        this.updateSelectedValue('style', selectedStyle ? selectedStyle.name : this.currentSelections.style);
      }

      getShopifyFileUrl(filename) {
        // Shopify Files are accessed via /cdn/shop/files/ path on custom domain
        // Pattern: //{domain}/cdn/shop/files/{filename} or /cdn/shop/files/{filename}
        // Example: //racertees.com/cdn/shop/files/ProV4-FontSwatch-F08_60x.png?v=4542434006961133438
        // Using relative path which will work with the current domain
        return `/cdn/shop/files/${encodeURIComponent(filename)}`;
      }

      getStyleSwatchUrl(styleId, mainColorId, borderColorId) {
        // Format: ProV4-StyleSwatch-{STYLEID}-{MAINCOLORID}-{BORDERCOLORID}.jpg
        // Example: ProV4-StyleSwatch-01-DB-WT.jpg
        // When main color is "US" (Old Glory), border color is not included in filename
        // Example: ProV4-StyleSwatch-01-US.jpg
        let filename;
        if (mainColorId === 'US') {
          filename = `ProV4-StyleSwatch-${styleId}-${mainColorId}.jpg`;
        } else {
          filename = `ProV4-StyleSwatch-${styleId}-${mainColorId}-${borderColorId}.jpg`;
        }
        return this.getShopifyFileUrl(filename);
      }

      getProofImageUrl(fontId, styleId, mainColorId, borderColorId) {
        // Format: ProV4-Proof-F{FONTID}-S{STYLEID}-{MAINCOLORID}-{BORDERCOLORID}.jpg
        // When main color is "US" (Old Glory), border color is not included in filename
        // Example: ProV4-Proof-F01-S01-US.jpg
        let filename;
        if (mainColorId === 'US') {
          filename = `ProV4-Proof-F${fontId}-S${styleId}-${mainColorId}.jpg`;
        } else {
          filename = `ProV4-Proof-F${fontId}-S${styleId}-${mainColorId}-${borderColorId}.jpg`;
        }
        return this.getShopifyFileUrl(filename);
      }

      updateProductImage() {
        // When main color is US, border color is not used in the filename
        const proofImageUrl = this.getProofImageUrl(
          this.currentSelections.font,
          this.currentSelections.style,
          this.currentSelections.mainColor,
          this.currentSelections.mainColor === 'US' ? '' : this.currentSelections.borderColor
        );

        // Find the first product media image and update it
        const mediaGallery = document.querySelector('media-gallery');
        if (mediaGallery) {
          const firstImage = mediaGallery.querySelector('img');
          if (firstImage) {
            // Create a new image to preload
            const img = new Image();
            img.onload = () => {
              // Update srcset and src after image loads
              firstImage.srcset = `${proofImageUrl} 550w, ${proofImageUrl} 1100w, ${proofImageUrl} 1445w, ${proofImageUrl} 1680w, ${proofImageUrl} 2048w`;
              firstImage.src = proofImageUrl;
              firstImage.alt = `ProV4 Proof - Font ${this.currentSelections.font}, Style ${this.currentSelections.style}, Main ${this.currentSelections.mainColor}, Border ${this.currentSelections.borderColor}`;
            };
            img.onerror = () => {
              console.warn('Proof image not found:', proofImageUrl);
            };
            img.src = proofImageUrl;
          }
        }
      }

      updateSelectedValue(type, value) {
        const selectedValueSpan = this.querySelector(`[data-selected-value="${type}"]`);
        if (selectedValueSpan) {
          selectedValueSpan.textContent = value;
        }
      }

      toggleBorderColorSelector(hide) {
        // Show or hide the border color selector based on main color selection
        const borderColorFieldset = this.querySelector('[data-customization-type="borderColor"]');
        if (borderColorFieldset) {
          if (hide) {
            borderColorFieldset.style.display = 'none';
          } else {
            borderColorFieldset.style.display = '';
          }
        }
      }

      updateHiddenInputs() {
        const fontInput = document.getElementById(`prov4-font-input-${this.sectionId}`);
        const mainColorInput = document.getElementById(`prov4-main-color-input-${this.sectionId}`);
        const borderColorInput = document.getElementById(`prov4-border-color-input-${this.sectionId}`);
        const styleInput = document.getElementById(`prov4-style-input-${this.sectionId}`);

        // Store display names (not IDs) for cart display
        if (fontInput) {
          // Font ID is already the display name (e.g., "01")
          fontInput.value = this.currentSelections.font;
        }
        
        if (mainColorInput) {
          const selectedColor = this.customizationData.mainColors.find(c => c.id === this.currentSelections.mainColor);
          mainColorInput.value = selectedColor ? selectedColor.name : this.currentSelections.mainColor;
        }
        
        if (borderColorInput) {
          // Only set border color value if main color is not US
          if (this.currentSelections.mainColor !== 'US') {
            const selectedBorderColor = this.customizationData.borderColors.find(c => c.id === this.currentSelections.borderColor);
            borderColorInput.value = selectedBorderColor ? selectedBorderColor.name : this.currentSelections.borderColor;
          } else {
            borderColorInput.value = ''; // Clear border color when US is selected
          }
        }
        
        if (styleInput) {
          // Get display name for style (e.g., "10G" -> "10-Gold")
          const selectedStyle = this.customizationData.styles.find(s => s.id === this.currentSelections.style);
          styleInput.value = selectedStyle ? selectedStyle.name : this.currentSelections.style;
        }
      }

      setupEventListeners() {
        this.addEventListener('change', (e) => {
          if (e.target.hasAttribute('data-customization-type')) {
            const type = e.target.getAttribute('data-customization-type');
            const value = e.target.value;

            this.currentSelections[type] = value;
            this.updateHiddenInputs();

            // Update selected value display
            if (type === 'font') {
              this.updateSelectedValue('font', value);
            } else if (type === 'mainColor') {
              const selectedColor = this.customizationData.mainColors.find(c => c.id === value);
              this.updateSelectedValue('mainColor', selectedColor ? selectedColor.name : '');
              
              // Show/hide border color selector based on main color selection
              this.toggleBorderColorSelector(value === 'US');
            } else if (type === 'borderColor') {
              const selectedColor = this.customizationData.borderColors.find(c => c.id === value);
              this.updateSelectedValue('borderColor', selectedColor ? selectedColor.name : '');
            } else if (type === 'style') {
              // Get display name for style (e.g., "10G" -> "10-Gold")
              const selectedStyle = this.customizationData.styles.find(s => s.id === value);
              this.updateSelectedValue('style', selectedStyle ? selectedStyle.name : value);
            }

            // If font, main color, or border color changes, update style swatches
            if (type === 'font' || type === 'mainColor' || type === 'borderColor') {
              this.updateStyleSelector();
            }

            // Always update product image when any selection changes
            this.updateProductImage();
          }
        });

        // Decal sizing selector event listeners
        const decalSizingInputs = this.querySelectorAll('input[name^="prov4-decal-sizing-"]');
        decalSizingInputs.forEach(input => {
          input.addEventListener('change', (e) => {
            const value = e.target.value;
            const hiddenInput = document.getElementById(`prov4-decal-sizing-input-${this.sectionId}`);
            if (hiddenInput) {
              hiddenInput.value = value;
            }
            this.updateSelectedValue('decalSizing', value);
            this.toggleCustomSizingDetails(value === 'Custom Size');
          });
        });
      }

      toggleCustomSizingDetails(show) {
        const customSizingDetails = document.getElementById(`prov4-custom-sizing-details-${this.sectionId}`);
        if (customSizingDetails) {
          if (show) {
            customSizingDetails.style.display = '';
            const input = customSizingDetails.querySelector('input');
            if (input) {
              input.disabled = false;
              input.removeAttribute('disabled');
            }
          } else {
            customSizingDetails.style.display = 'none';
            const input = customSizingDetails.querySelector('input');
            if (input) {
              input.disabled = true;
              input.value = ''; // Clear value when hidden
            }
          }
        }
      }

      setupDecalSizingSelector() {
        // Initialize the decal sizing selector display
        const decalSizingInputs = this.querySelectorAll('input[name^="prov4-decal-sizing-"]');
        const checkedInput = Array.from(decalSizingInputs).find(input => input.checked);
        if (checkedInput) {
          const value = checkedInput.value;
          this.updateSelectedValue('decalSizing', value);
          this.toggleCustomSizingDetails(value === 'Custom Size');
        }
      }

      setupVariantChangeListener() {
        // Try to use pub/sub system if available
        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data && event.data.sectionId === this.sectionId && event.data.variant) {
              this.updateFieldsBasedOnVariant(event.data.variant);
            }
          });
        }
        
        // Also set up a fallback: watch for variant-selects changes directly
        const variantSelects = document.querySelector('variant-selects');
        if (variantSelects) {
          // Initial check
          this.checkVariantFromSelects(variantSelects);
          
          // Watch for changes to the variant data script
          this.variantObserver = new MutationObserver(() => {
            this.checkVariantFromSelects(variantSelects);
          });
          
          this.variantObserver.observe(variantSelects, {
            childList: true,
            subtree: true,
            characterData: true
          });
        }
      }

      checkVariantFromSelects(variantSelects) {
        const variantDataScript = variantSelects.querySelector('[data-selected-variant]');
        if (variantDataScript) {
          try {
            const variant = JSON.parse(variantDataScript.textContent);
            this.updateFieldsBasedOnVariant(variant);
          } catch (e) {
            // Silently fail - variant might not be selected yet
          }
        }
      }

      updateFieldsBasedOnVariant(variant = null) {
        // Get current variant if not provided
        if (!variant) {
          const variantSelects = document.querySelector('variant-selects');
          if (variantSelects) {
            const variantDataScript = variantSelects.querySelector('[data-selected-variant]');
            if (variantDataScript) {
              try {
                variant = JSON.parse(variantDataScript.textContent);
              } catch (e) {
                console.error('Error parsing variant data:', e);
                return;
              }
            } else {
              return;
            }
          } else {
            return;
          }
        }

        const variantTitle = variant.title || '';
        const hasNumbers = variantTitle.toLowerCase().includes('numbers');
        const hasNames = variantTitle.toLowerCase().includes('names');

        // Get field containers
        const numbersFields = this.querySelectorAll('.prov4-numbers-field');
        const namesFields = this.querySelectorAll('.prov4-names-field');

        // Show/hide and enable/disable numbers fields
        numbersFields.forEach(field => {
          if (hasNumbers) {
            field.style.display = '';
            const input = field.querySelector('input');
            if (input) {
              input.disabled = false;
              input.removeAttribute('disabled');
            }
          } else {
            field.style.display = 'none';
            const input = field.querySelector('input');
            if (input) {
              input.disabled = true;
              input.value = ''; // Clear value when hidden
            }
          }
        });

        // Show/hide and enable/disable names fields
        namesFields.forEach(field => {
          if (hasNames) {
            field.style.display = '';
            const input = field.querySelector('input');
            if (input) {
              input.disabled = false;
              input.removeAttribute('disabled');
            }
          } else {
            field.style.display = 'none';
            const input = field.querySelector('input');
            if (input) {
              input.disabled = true;
              input.value = ''; // Clear value when hidden
            }
          }
        });
      }

      setupStickyImage() {
        // Only apply on mobile devices (max-width: 749px)
        if (window.innerWidth > 749) {
          return;
        }

        // Find the product media wrapper
        const mediaWrapper = document.querySelector('.product__media-wrapper');
        if (!mediaWrapper) {
          return;
        }

        // Find the section divider before customer details (this is always visible)
        // This marks the boundary where sticky should stop
        const sectionDivider = this.querySelector('.prov4-section-divider');
        if (!sectionDivider) {
          return;
        }

        // Find the customer details section to check when inputs come into view
        const customerDetails = this.querySelector('.prov4-customer-details');
        
        // Add sticky class to enable sticky positioning
        mediaWrapper.classList.add('prov4-sticky-image');

        // Store the image height for smooth transition
        let imageHeight = mediaWrapper.offsetHeight;

        // Calculate the stop point (where divider will be when it reaches top of viewport)
        let ticking = false;

        const handleScroll = () => {
          if (!ticking) {
            window.requestAnimationFrame(() => {
              // Update image height in case it changed
              imageHeight = mediaWrapper.offsetHeight;
              
              const dividerRect = sectionDivider.getBoundingClientRect();
              
              // Calculate when to start disabling sticky
              // We want to disable sticky when the divider is about to reach the top
              // Account for image height so it scrolls up before covering inputs
              // Add a small buffer (20px) to ensure inputs are never covered
              const stopPoint = imageHeight + 20;
              
              // Only disable sticky when divider is approaching the top (accounting for image height)
              // This ensures the image scrolls up before it can cover any input fields
              if (dividerRect.top <= stopPoint) {
                // Divider is approaching the top - disable sticky with smooth transition
                if (!mediaWrapper.classList.contains('prov4-sticky-disabled')) {
                  // Set custom property for smooth scroll up
                  mediaWrapper.style.setProperty('--scroll-up-distance', `-${imageHeight}px`);
                  mediaWrapper.classList.add('prov4-sticky-disabled');
                  
                  // After transition completes, change to relative positioning
                  setTimeout(() => {
                    if (mediaWrapper.classList.contains('prov4-sticky-disabled')) {
                      mediaWrapper.classList.add('prov4-transition-complete');
                    }
                  }, 500); // Match CSS transition duration
                }
              } else {
                // Divider is still well below the stop point - enable sticky
                if (mediaWrapper.classList.contains('prov4-sticky-disabled')) {
                  // Remove transition complete class first to allow smooth return
                  mediaWrapper.classList.remove('prov4-transition-complete');
                  // Small delay to allow transform to reset
                  requestAnimationFrame(() => {
                    mediaWrapper.classList.remove('prov4-sticky-disabled');
                    mediaWrapper.style.removeProperty('--scroll-up-distance');
                  });
                }
              }

              ticking = false;
            });
            ticking = true;
          }
        };

        // Initial check
        handleScroll();

        // Throttled scroll listener
        window.addEventListener('scroll', handleScroll, { passive: true });

        // Store scroll handler for cleanup
        this.stickyImageScrollHandler = handleScroll;

        // Handle window resize to re-check if we're still on mobile
        const handleResize = () => {
          if (window.innerWidth > 749) {
            // Desktop - remove sticky behavior
            mediaWrapper.classList.remove('prov4-sticky-image', 'prov4-sticky-disabled');
            if (this.stickyImageScrollHandler) {
              window.removeEventListener('scroll', this.stickyImageScrollHandler);
            }
            window.removeEventListener('resize', handleResize);
          } else {
            // Still mobile - re-check sticky state
            handleScroll();
          }
        };

        window.addEventListener('resize', handleResize);
        this.stickyImageResizeHandler = handleResize;
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
        if (this.variantObserver) {
          this.variantObserver.disconnect();
        }
        if (this.stickyImageScrollHandler) {
          window.removeEventListener('scroll', this.stickyImageScrollHandler);
        }
        if (this.stickyImageResizeHandler) {
          window.removeEventListener('resize', this.stickyImageResizeHandler);
        }
      }
    }
  );
}

